import { CiSearch } from "react-icons/ci";
import {
  Button,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  Row,
} from "reactstrap";
import { CgOptions } from "react-icons/cg";
import { useEffect, useState } from "react";
import { categories, discountFilters } from "../../dummyData";
import LabeledCheckedInput from "./LabeledCheckedInput";
import ButtonNavigator from "../navigators/ButtonNavigator";
import DatePickInput from "./DatePickInput";
import SimpleDropdownInput from "./SimpleDropdownInput";
import { IoFemale, IoMaleOutline } from "react-icons/io5";
import { IoMdTransgender } from "react-icons/io";
import { useTranslation } from "../../languages";
import { DiscountIcon } from "../../assets/designedPickedIcons";
import { useNavigate } from "react-router-dom";

const FilterBasedSearch = ({
  onSearch,
  allPlacesData=[],
  setAllPlacesData=()=>{},
  originalPlacesData=[], // 🔹 pass unfiltered master list for resetting filters
}) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [simpleSearch, setSimpleSearch] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const navigateTo=useNavigate();

  const [categoriesOptions, setCategoriesOptions] = useState(
    categories.map((i) => ({ ...i, isSelected: false }))
  );
  const [discountOptions, setDiscountOptions] = useState(
    discountFilters.map((i) => ({ ...i, isSelected: false }))
  );
  const [hoursTypeFlowAt, setHoursTypeFlowAt] = useState(0);
  const [dateSelected, setDateSelected] = useState(new Date());
  const [genderSelected, setGenderSelected] = useState("");

  const genderOptions = [
    { title: "Male", icon: <IoMaleOutline color="dodgerblue" size={24} /> },
    { title: "Female", icon: <IoFemale color="magenta" size={24} /> },
    { title: "Other", icon: <IoMdTransgender color="pink" size={24} /> },
  ];

  const { t } = useTranslation();

  // 🔍 Live search (text only)
  useEffect(() => {
    if (!simpleSearch.trim()) {
      setFilteredResults([]);
      setAllPlacesData?.(originalPlacesData);
      return;
    }

    const filtered = [...originalPlacesData].filter((place) =>
      place.name?.toLowerCase().includes(simpleSearch.toLowerCase())
    );
    setFilteredResults(filtered);
    setAllPlacesData(filtered);
  }, [simpleSearch]);

  // ✅ Apply advanced filters (category, discount, gender, hours)
  const applyFilters = () => {
    let filtered = [...originalPlacesData];

    // 1️⃣ Search text
    if (simpleSearch.trim()) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(simpleSearch.toLowerCase())
      );
    }

    // 2️⃣ Categories
    const selectedCategories = categoriesOptions
      .filter((c) => c.isSelected)
      .map((c) => c.name?.toLowerCase());
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((item) =>
        item.categories?.some((cat) =>
          selectedCategories.includes(cat.name?.toLowerCase())
        )
      );
    }

    // 3️⃣ Discounts
    const selectedDiscounts = discountOptions
      .filter((d) => d.isSelected)
      .map((d) => d.title?.toLowerCase());
    if (selectedDiscounts.length > 0) {
      filtered = filtered.filter((item) =>
        item.offers?.some((offer) =>
          selectedDiscounts.includes(offer?.title?.toLowerCase())
        )
      );
    }

    // 4️⃣ Gender preference
    if (genderSelected?.title) {
      filtered = filtered.filter((item) =>
        item.genderPreference?.includes(genderSelected.title.toLowerCase())
      );
    }

    // 5️⃣ Hours (Open Now)
    if (hoursTypeFlowAt === 1) {
      const now = new Date();
      const currentDay = now.toLocaleString("en-US", { weekday: "long" });
      const currentTime = now.toTimeString().slice(0, 5);
      filtered = filtered.filter((item) => {
        const todayAvailability = item.availability?.find(
          (a) => a.day?.toLowerCase() === currentDay.toLowerCase()
        );
        if (!todayAvailability) return false;
        return (
          currentTime >= todayAvailability.openTime &&
          currentTime <= todayAvailability.closeTime
        );
      });
    }

    // ✅ Apply and close
    setAllPlacesData(filtered);
    setOptionsOpen(false);
  };

  // 🧹 Clear all filters
  const clearFilters = () => {
    setCategoriesOptions(categories.map((i) => ({ ...i, isSelected: false })));
    setDiscountOptions(
      discountFilters.map((i) => ({ ...i, isSelected: false }))
    );
    setGenderSelected("");
    setHoursTypeFlowAt(0);
    setSimpleSearch("");
    setAllPlacesData(originalPlacesData);
    setOptionsOpen(false);
  };

  return (
    <>
      <div className="search-bar d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center full-width gap-2 position-relative">
          <CiSearch color="rgba(44, 44, 44, 1)" size={24} />
          <Input
            value={simpleSearch}
            onChange={(e) => setSimpleSearch(e.target.value)}
            className="bg-transparent full-height border-none p-0"
            placeholder="Search"
          />

          {/* 🔽 Dropdown search results */}
          {filteredResults?.length > 0 && (
            <div className="position-absolute bg-white place-search-dropdown shadow rounded p-2 w-100 z-10">
              {filteredResults.map((item, idx) => (
                <Button
                  key={idx}
                  onClick={() => {
                   navigateTo("/directions",{state:item})
                    setSimpleSearch(item.name);
                    setFilteredResults([]);
                  }}
                  className="bg-transparent text-black d-flex align-items-center gap-2 border-0 w-100 text-start"
                >
                  <img
                    src={item?.photos?.[0]}
                    height={60}
                    width={80}
                    className="object-fit-cover rounded"
                    alt={item?.name}
                  />
                  <div className="text-start">
                    <h6 className="m-0">{item?.name}</h6>
                    <small className="text-muted">
                      {item?.location?.streetAddress}
                    </small>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* ⚙️ Filter Button */}
        <Button
          onClick={() => setOptionsOpen(true)}
          className="bg-transparent border-none p-0 mx-3"
        >
          <CgOptions color="rgba(33, 0, 93, 1)" size={22} />
        </Button>
      </div>

      {/* 🧭 Modal for Filters */}
      <Modal
        centered
        size="lg"
        fade={false}
        isOpen={optionsOpen}
        toggle={() => setOptionsOpen((prev) => !prev)}
      >
        <ModalBody
          className="bg-white border-none px-lg-4"
          style={{ borderRadius: "40px" }}
        >
          <Container fluid>
            <h3 className="text-center mt-3 mb-4 figma-shadowed-black-text">
              {t("Filter")}
            </h3>

            {/* Category */}
            <h5 className="color-primary-black-3 mb-3">{t("category")}</h5>
            <div className="gap-3 grid-container-4 justify-items-between">
              {categoriesOptions.map((item, index) => (
                <div key={index}>
                  <LabeledCheckedInput
                    iconColor={item?.color}
                    iconSize={30}
                    label={t(item?.key)}
                    value={item?.isSelected}
                    Icon={item?.icon}
                    setValue={(v) => {
                      const updated = [...categoriesOptions];
                      updated[index].isSelected = v;
                      setCategoriesOptions(updated);
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Hours + Gender */}
            <h5 className="color-primary-black-3 mt-5 mb-3">{t("hours")}</h5>
            <ButtonNavigator
              flowAt={hoursTypeFlowAt}
              setFlowAt={setHoursTypeFlowAt}
              options={[
                { title: "Any", flowAt: 0 },
                { title: "Open Now", flowAt: 1 },
                { title: "Custom", flowAt: 2 },
              ]}
            />
            <Row className="g-3 mt-3">
              <Col md={6}>
                <DatePickInput
                  value={dateSelected}
                  setValue={setDateSelected}
                  placeholder={"Select Date"}
                />
              </Col>
              <Col md={6}>
                <SimpleDropdownInput
                  dropdownClass="dropdown-menu-modified"
                  renderOption={(item) => (
                    <div className="d-flex align-items-center gap-2">
                      {item?.icon} {item?.title}
                    </div>
                  )}
                  options={genderOptions}
                  placeholder={"Select gender"}
                  value={genderSelected}
                  renderValue={(v) => (
                    <div className="d-flex align-items-center gap-2">
                      {v?.icon} {v?.title}
                    </div>
                  )}
                  setValue={setGenderSelected}
                />
              </Col>
            </Row>

            {/* Discounts */}
            <h5 className="color-primary-black-3 mb-3 mt-4">{t("discount")}</h5>
            <div className="gap-3 grid-container-3">
              {discountOptions.map((item, key) => (
                <LabeledCheckedInput
                  key={key}
                  label={
                    <div className="d-flex align-items-center gap-2">
                      <DiscountIcon
                        size={{ height: 28, width: 20 }}
                        color={item?.color}
                      />
                      {item?.title}
                    </div>
                  }
                  iconColor={item?.color}
                  value={item?.isSelected}
                  iconSize={30}
                  setValue={(v) => {
                    const updated = [...discountOptions];
                    updated[key].isSelected = v;
                    setDiscountOptions(updated);
                  }}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="d-flex align-items-center flex-wrap justify-content-center gap-3 mt-5 mb-3">
              <Button
                onClick={clearFilters}
                style={{
                  border: "1px solid rgba(182, 182, 182, 0.3)",
                  borderRadius: "0.5rem",
                }}
                className="full-width py-3 bg-white color-primary-blue outer-transparent-shadow"
              >
                {t("clear")}
              </Button>
              <Button
                onClick={applyFilters}
                style={{ borderRadius: "0.5rem" }}
                className="full-width py-3 bg-primary-blue border-none text-white outer-transparent-shadow"
              >
                {t("apply")}
              </Button>
            </div>
          </Container>
        </ModalBody>
      </Modal>
    </>
  );
};

export default FilterBasedSearch;
